import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { PropTypes } from 'prop-types';
import { isInViewport } from '../../utility/domHelpers';
import './Video.scss';

export const [VIDEO_STATUS_PLAYING, VIDEO_STATUS_PAUSED] = ['play', 'pause'];

const Video = ({
  srcMap,
  checkIsInView,
  status,
  subtitles,
  showSubtitle,
  onDurationChange,
  controlsList,
  onVideoScrollEvent,
  stopOnScroll,
  ...videoProps
}) => {
  const className = 'c-Video';
  const videoRef = useRef(null);

  const handleScroll = () => {
    if (!isInViewport(videoRef) && stopOnScroll) {
      if (onVideoScrollEvent) {
        onVideoScrollEvent();
      } else {
        videoRef.current.pause();
      }
    }
  };

  useEffect(() => {
    if (checkIsInView) {
      if (videoRef) {
        window.addEventListener('scroll', handleScroll);
      }

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, checkIsInView]);

  useEffect(() => {
    const changePlayingStatus = () => {
      if (status === VIDEO_STATUS_PLAYING) {
        videoRef.current.play();
      } else if (status === VIDEO_STATUS_PAUSED) {
        videoRef.current.pause();
      }
    };

    changePlayingStatus();
  }, [status]);

  useLayoutEffect(() => {
    const changePlayingStatus = () => {
      if (status === VIDEO_STATUS_PLAYING) {
        videoRef.current.play();
      } else if (status === VIDEO_STATUS_PAUSED) {
        videoRef.current.pause();
      }
    };

    changePlayingStatus();
  }, [status]);

  useEffect(() => {
    onDurationChange(videoRef.current);
  }, [videoProps, onDurationChange]);

  useLayoutEffect(() => {
    onDurationChange(videoRef.current);
  }, [videoProps, onDurationChange]);

  // Hide context menu
  useEffect(() => {
    if (videoRef) {
      videoRef.current.addEventListener('contextmenu', (event) => {
        event.preventDefault();
      });
    }
  }, [srcMap]);

  return (
    <div className={className}>
      <video
        ref={videoRef}
        {...videoProps}
        data-keepplaying
        preload="none"
        disablePictureInPicture
        controlsList={controlsList}
      >
        {srcMap?.map(({ src, type }, key) => {
          return <source key={key} src={src} type={type} />;
        })}
        {showSubtitle &&
          subtitles?.map((subtitle, key) => {
            const { label, language, src, isDefault } = subtitle || {};
            return (
              <track
                label={label}
                kind="subtitles"
                srcLang={language}
                src={src}
                default={isDefault}
                key={key}
              />
            );
          })}
        Your browser does not support video tag.
      </video>
    </div>
  );
};

export const VideoDataType = {
  srcMap: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  status: PropTypes.string,
  checkIsInView: PropTypes.bool,
  showSubtitle: PropTypes.bool,
  subtitles: PropTypes.array,
  onDurationChange: PropTypes.func,
  controlsList: PropTypes.string,
  stopOnScroll: PropTypes.bool,
};

Video.defaultProps = {
  checkIsInView: true,
  showSubtitle: false,
  onDurationChange: () => {},
  controlsList: 'nodownload',
  stopOnScroll: true,
};

Video.propTypes = VideoDataType;

export default Video;
